import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFavorites, useRemoveFavorite } from '@/hooks';
import { Card, CardContent, Button, Skeleton } from '@/components/ui';
import { formatRelativeTime } from '@/lib/utils';
import { Heart, Eye, Clock, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const getVisibilityLabel = (visibility: string) => {
  switch (visibility) {
    case 'anonymous':
      return '匿名公开';
    case 'public':
      return '公开';
    case 'editors':
      return '编辑者可见';
    case 'private':
      return '私有';
    default:
      return '公开';
  }
};

export function Favorites() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useFavorites(page, 10);
  const removeFavorite = useRemoveFavorite();

  const handleRemove = (slug: string, title: string) => {
    if (!confirm(`确定要取消收藏 "${title}" 吗？`)) return;

    removeFavorite.mutate(slug, {
      onSuccess: () => toast.success('已取消收藏'),
      onError: () => toast.error('操作失败'),
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12 space-y-6">
        <Skeleton className="h-8 w-32" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-red-500">加载失败</p>
      </div>
    );
  }

  const favorites = data?.items || [];

  return (
    <div className="max-w-4xl mx-auto py-12 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Heart className="h-6 w-6 text-red-500" />
          我的收藏
        </h1>
        <span className="text-sm text-muted-foreground">
          共 {data?.total || 0} 个收藏
        </span>
      </div>

      {favorites.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">暂无收藏的页面</p>
            <Link to="/pages">
              <Button variant="outline">浏览页面</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {favorites.map((favorite) => (
            <Card key={favorite.id}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/pages/${favorite.page?.slug}`}
                      className="text-lg font-semibold hover:underline"
                    >
                      {favorite.page?.title || '已删除的页面'}
                    </Link>
                    {favorite.page && (
                      <>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {getVisibilityLabel(favorite.page.visibility)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            收藏于 {formatRelativeTime(favorite.created_at)}
                          </span>
                        </div>
                        {favorite.page.tags && favorite.page.tags.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {favorite.page.tags.map((tag) => (
                              <Link
                                key={tag.id}
                                to={`/tags/${tag.name}`}
                                className="px-2 py-0.5 bg-muted rounded text-xs hover:bg-muted/80"
                              >
                                {tag.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleRemove(favorite.page?.slug || '', favorite.page?.title || '')}
                    disabled={removeFavorite.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <span className="py-2 px-4 text-sm text-muted-foreground">
            第 {page} / {data.totalPages} 页
          </span>
          <Button
            variant="outline"
            disabled={page === data.totalPages}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </div>
      )}
    </div>
  );
}
